// src/hooks/useDataIntegrity.js
import { useMemo } from 'react';
import { CRITERIA } from '../constants';

/**
 * Runs data integrity checks on the score snapshot.
 * Returns an array of check results: { id, label, status, detail }
 * status: 'pass' | 'warn' | 'fail'
 */
export const useDataIntegrity = (allScores, contestants, members, presenceDocs) => {
  return useMemo(() => {
    const checks = [];
    const allScoreDocs = [];

    // Flatten allScores into a list of { contestantId, userId, data }
    for (const [contestantId, userScores] of Object.entries(allScores || {})) {
      for (const [userId, data] of Object.entries(userScores)) {
        allScoreDocs.push({ contestantId, userId, data });
      }
    }

    // 1. Score boundaries — every criterion value must be 0-10
    const outOfBounds = allScoreDocs.filter(({ data }) =>
      CRITERIA.some(c => data[c.id] !== undefined && (data[c.id] < 0 || data[c.id] > 10))
    );
    checks.push({
      id: 'boundaries',
      label: 'Score boundaries (all 0-10)',
      status: outOfBounds.length > 0 ? 'fail' : 'pass',
      detail: outOfBounds.length > 0
        ? `${outOfBounds.length} scores out of range`
        : `${allScoreDocs.length}/${allScoreDocs.length}`,
    });

    // 2. Formula validation — recalculate overall, compare to stored
    // Tolerance is 0.02 (not 0.01) to absorb float-precision noise when the
    // stored overall was written as a rounded value. Real formula errors
    // are always much larger than one criterion-weight ULP.
    const formulaMismatches = allScoreDocs.filter(({ data }) => {
      if (data.overall === undefined) return false;
      let expected = 0;
      let hasAll = true;
      CRITERIA.forEach(c => {
        if (data[c.id] !== undefined) expected += parseFloat(data[c.id]) * c.weight;
        else hasAll = false;
      });
      if (!hasAll) return false;
      return Math.abs(expected - data.overall) > 0.02;
    });
    checks.push({
      id: 'formula',
      label: 'Overall = weighted avg',
      status: formulaMismatches.length > 0 ? 'fail' : 'pass',
      detail: formulaMismatches.length > 0
        ? `${formulaMismatches.length} mismatches`
        : `${allScoreDocs.length}/${allScoreDocs.length}`,
    });

    // 3. Orphaned scores — score contestantId not in contestants list
    const contestantIds = new Set((contestants || []).map(c => String(c.id)));
    const orphaned = allScoreDocs.filter(({ contestantId }) => !contestantIds.has(String(contestantId)));
    checks.push({
      id: 'orphaned',
      label: 'No orphaned scores',
      status: orphaned.length > 0 ? 'fail' : 'pass',
      detail: orphaned.length > 0
        ? `${orphaned.length} found`
        : '0 found',
    });

    // 4. Missing contestId — score docs without contestId field
    const missingContestId = allScoreDocs.filter(({ data }) => !data.contestId);
    checks.push({
      id: 'contestId',
      label: 'All scores have contestId',
      status: missingContestId.length > 0 ? 'fail' : 'pass',
      detail: missingContestId.length > 0
        ? `${missingContestId.length} missing`
        : `${allScoreDocs.length}/${allScoreDocs.length}`,
    });

    // 5. Ranking consistency — sort by recalculated avg, check order
    const rankings = (contestants || []).map(c => {
      const scores = allScores?.[c.id] || {};
      const voters = Object.values(scores).filter(s => s.overall !== undefined);
      if (voters.length === 0) return { id: c.id, avg: 0, hasVotes: false };
      const avg = voters.reduce((sum, s) => sum + s.overall, 0) / voters.length;
      return { id: c.id, avg, hasVotes: true };
    }).filter(r => r.hasVotes).sort((a, b) => b.avg - a.avg);

    // Recalculate with raw criteria
    const recalcRankings = (contestants || []).map(c => {
      const scores = allScores?.[c.id] || {};
      const voters = Object.values(scores);
      const valid = voters.filter(s => CRITERIA.every(cr => s[cr.id] !== undefined));
      if (valid.length === 0) return { id: c.id, avg: 0, hasVotes: false };
      const avg = valid.reduce((sum, s) => {
        let total = 0;
        CRITERIA.forEach(cr => { total += parseFloat(s[cr.id]) * cr.weight; });
        return sum + total;
      }, 0) / valid.length;
      return { id: c.id, avg, hasVotes: true };
    }).filter(r => r.hasVotes).sort((a, b) => b.avg - a.avg);

    const rankingMatch = rankings.length === recalcRankings.length &&
      rankings.every((r, i) => r.id === recalcRankings[i]?.id);
    checks.push({
      id: 'rankings',
      label: 'Rankings consistent',
      status: rankings.length === 0 ? 'pass' : (rankingMatch ? 'pass' : 'warn'),
      detail: rankings.length === 0 ? 'no votes yet' : (rankingMatch ? 'verified' : 'mismatch detected'),
    });

    // 6. Voting participation — members with 0 votes
    const voterIds = new Set(allScoreDocs.map(s => s.userId));
    const nonVoters = (members || []).filter(m => !voterIds.has(m.id));
    checks.push({
      id: 'participation',
      label: 'Voting participation',
      status: nonVoters.length > 0 ? 'warn' : 'pass',
      detail: nonVoters.length > 0
        ? `${nonVoters.length} haven't voted: ${nonVoters.map(m => m.name).join(', ')}`
        : 'all members voted',
    });

    // 7. Stale presence — lastActive > 5 min ago
    const stale = (presenceDocs || []).filter(p => {
      const lastActive = p.lastActive?.toDate?.() ? p.lastActive.toDate().getTime() : new Date(p.lastActive).getTime();
      return Date.now() - lastActive > 5 * 60 * 1000;
    });
    checks.push({
      id: 'stalePresence',
      label: 'Stale presence',
      status: stale.length > 0 ? 'warn' : 'pass',
      detail: stale.length > 0
        ? `${stale.length} stale`
        : 'all fresh',
    });

    return checks;
  }, [allScores, contestants, members, presenceDocs]);
};
